import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { MainLayout } from "@/components/MainLayout";
import { usePortfolio } from "@/context/PortfolioContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { PageLoader } from "@/components/MoneyMachineLoader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import { API_URL, CHANNELS, EMPTY_FORM } from "@/components/opportunities/helpers";
import { OpportunityFormDialog } from "@/components/opportunities/OpportunityFormDialog";
import { OpportunityDetailModal } from "@/components/opportunities/OpportunityDetailModal";
import { RecommendedSection } from "@/components/opportunities/RecommendedSection";
import { MyDealsTab } from "@/components/opportunities/MyDealsTab";
import { DefiTab } from "@/components/opportunities/DefiTab";
import { Web3SecondariesTab } from "@/components/opportunities/Web3SecondariesTab";
import { StocksTab } from "@/components/opportunities/StocksTab";
import { OthersTab } from "@/components/opportunities/OthersTab";
import { CommoditiesTab } from "@/components/opportunities/CommoditiesTab";

const OpportunitiesPage = () => {
  const { opportunities, assets, loading: portfolioLoading, formatCurrency, fetchOpportunities, createOpportunity, deleteOpportunity } = usePortfolio();
  const { t } = useI18n();
  const { isDemo, user } = useAuth();
  const [searchParams] = useSearchParams();

  // Filter channels based on user's visible_channels preference
  const userVisibleChannels = user?.visible_channels || CHANNELS.map(c => c.id);
  const visibleChannels = CHANNELS.filter(c => userVisibleChannels.includes(c.id));

  const [activeChannel, setActiveChannel] = useState(visibleChannels[0]?.id || "my_deals");

  // Sync tab from URL query param
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && userVisibleChannels.includes(tab)) {
      setActiveChannel(tab);
    }
  }, [searchParams, userVisibleChannels]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Delete
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Alternatives
  const [alternatives, setAlternatives] = useState([]);
  const [altLastRefresh, setAltLastRefresh] = useState(null);
  const [altLoading, setAltLoading] = useState(false);
  const [altCategoryFilter, setAltCategoryFilter] = useState("all");
  const [altTypeFilter, setAltTypeFilter] = useState("all");
  const [altSearchQuery, setAltSearchQuery] = useState("");
  const [altMinTicket, setAltMinTicket] = useState("all");

  // Stocks - pre-filter from user preferred regions
  const [stockTrends, setStockTrends] = useState(null);
  const [stocksLastRefresh, setStocksLastRefresh] = useState(null);
  const [stocksLoading, setStocksLoading] = useState(false);
  const userRegions = user?.preferred_regions || [];
  // If user has no preferences OR has multiple regions, show "all" by default
  // If user has exactly one region, pre-select that region
  const [stocksRegionFilter, setStocksRegionFilter] = useState("all");
  const [eurUsdHistory, setEurUsdHistory] = useState([]);

  // Recommended & manual
  const [recommendedOpps, setRecommendedOpps] = useState([]);
  const [othersOpps, setOthersOpps] = useState([]);
  const [myDeals, setMyDeals] = useState([]);
  const [myDealsLoading, setMyDealsLoading] = useState(false);

  // Filters
  const [minTvl, setMinTvl] = useState("1000000");
  const [tokenFilter, setTokenFilter] = useState("");
  const [sortBy, setSortBy] = useState("apy");
  const [minApy, setMinApy] = useState("");
  const [maxApy, setMaxApy] = useState("");
  const [riskFilter, setRiskFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  // Detail modal
  const [detailOpp, setDetailOpp] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailFiles, setDetailFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [advisorResult, setAdvisorResult] = useState(null);

  // Save guard
  const [saving, setSaving] = useState(null);

  // ─── Fetch helpers ───
  const fetchAlternatives = useCallback(async () => {
    try { const resp = await axios.get(`${API_URL}/api/alternatives`, { withCredentials: true }); setAlternatives(resp.data.items || []); setAltLastRefresh(resp.data.last_refresh || null); } catch {}
  }, []);
  const fetchManualOpps = useCallback(async () => {
    try {
      const [othersResp, recResp] = await Promise.all([
        axios.get(`${API_URL}/api/opportunities/manual?category=others`, { withCredentials: true }),
        axios.get(`${API_URL}/api/opportunities/recommended`, { withCredentials: true }),
      ]);
      setOthersOpps(othersResp.data || []); setRecommendedOpps(recResp.data || []);
    } catch {}
  }, []);
  const fetchStockTrends = useCallback(async () => {
    try { const resp = await axios.get(`${API_URL}/api/opportunities/stock-trends`, { withCredentials: true }); setStockTrends(resp.data.data || null); setStocksLastRefresh(resp.data.last_refresh || null); } catch {}
  }, []);
  const fetchEurUsdHistory = useCallback(async () => {
    try { const resp = await axios.get(`${API_URL}/api/exchange-rates/eur-usd-history`, { withCredentials: true }); setEurUsdHistory(resp.data.history || []); } catch {}
  }, []);
  const fetchMyDeals = useCallback(async () => {
    setMyDealsLoading(true);
    try { const resp = await axios.get(`${API_URL}/api/opportunities/my-deals`, { withCredentials: true }); setMyDeals(resp.data || []); } catch {} finally { setMyDealsLoading(false); }
  }, []);

  useEffect(() => { if (!portfolioLoading) { fetchAlternatives(); fetchStockTrends(); fetchEurUsdHistory(); fetchManualOpps(); } }, [portfolioLoading, fetchAlternatives, fetchStockTrends, fetchEurUsdHistory, fetchManualOpps]);
  useEffect(() => { fetchMyDeals(); }, [fetchMyDeals]);

  // ─── Handlers ───
  const handleRefresh = async () => {
    setLoading(true); setCurrentPage(1);
    try { await fetchOpportunities({ minTvl: parseInt(minTvl), tokenFilter }); toast.success("Opportunities refreshed"); } catch { toast.error("Failed to refresh opportunities"); } finally { setLoading(false); }
  };

  const handleAltRefresh = async () => {
    setAltLoading(true);
    try { const resp = await axios.post(`${API_URL}/api/alternatives/refresh`, {}, { withCredentials: true }); toast.success(`Refreshed ${resp.data.count} alternatives`); await fetchAlternatives(); } catch { toast.error("Failed to refresh alternatives. Try again."); } finally { setAltLoading(false); }
  };

  const handleStocksRefresh = async () => {
    setStocksLoading(true);
    try { const resp = await axios.post(`${API_URL}/api/opportunities/stock-trends/refresh`, {}, { withCredentials: true }); setStockTrends(resp.data.data || null); setStocksLastRefresh(resp.data.last_refresh || null); toast.success("Stock trends updated"); fetchEurUsdHistory(); } catch (error) { toast.error(error.response?.data?.detail || "Failed to refresh stock trends"); } finally { setStocksLoading(false); }
  };

  const resetForm = () => setFormData({ ...EMPTY_FORM, category: activeChannel });

  const confirmDelete = (opp) => { setDeleteTarget(opp); setIsDeleteOpen(true); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try { await deleteOpportunity(deleteTarget.opportunity_id); toast.success("Opportunity deleted"); fetchManualOpps(); fetchMyDeals(); } catch { toast.error("Failed to delete opportunity"); } finally { setIsDeleteOpen(false); setDeleteTarget(null); }
  };

  const handleSave = async () => {
    try {
      const payload = { ...formData, apy: parseFloat(formData.apy) || 0, tvl: parseFloat(formData.tvl) || 0, risk_score: formData.risk_score };
      if (isEditMode) { await axios.put(`${API_URL}/api/opportunities/${isEditMode}`, payload, { withCredentials: true }); toast.success("Opportunity updated"); }
      else { await createOpportunity(payload); toast.success("Opportunity added"); }
      setIsAddOpen(false); setIsEditMode(false); resetForm(); fetchManualOpps(); fetchMyDeals();
    } catch { toast.error(isEditMode ? "Failed to update" : "Failed to add"); }
  };

  const openDetail = async (opp) => {
    setDetailOpp(opp); setDetailFiles([]); setAdvisorResult(null); setIsDetailOpen(true);
    try { const resp = await axios.get(`${API_URL}/api/opportunities/${opp.opportunity_id}`, { withCredentials: true }); setDetailFiles(resp.data.files || []); } catch {}
  };

  const openEdit = (opp) => {
    setFormData({ name: opp.name || "", category: opp.category || "others", protocol: opp.protocol || "", chain: opp.chain || "", apy: opp.apy?.toString() || "", tvl: opp.tvl?.toString() || "", token: opp.token || "", opportunity_type: "manual", risk_score: opp.risk_score || 50, risk_notes: opp.risk_notes || "", url: opp.url || "", description: opp.description || "", image_url: opp.image_url || "", expected_return: opp.expected_return || "", notes: opp.notes || "", contact: opp.contact || "", price: opp.price || "", ticket: opp.ticket || "", custom_type: opp.custom_type || "", currency: opp.currency || "USD" });
    setIsEditMode(opp.opportunity_id); setIsAddOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file || !detailOpp) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf"];
    if (!allowed.includes(file.type)) { toast.error("Only images (JPEG, PNG, WebP, GIF) and PDFs are allowed"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const resp = await axios.post(`${API_URL}/api/opportunities/${detailOpp.opportunity_id}/files`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }); setDetailFiles(prev => [...prev, resp.data]); toast.success("File uploaded"); } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  const handleFileDelete = async (fileId) => {
    if (!detailOpp) return;
    try { await axios.delete(`${API_URL}/api/opportunities/${detailOpp.opportunity_id}/files/${fileId}`, { withCredentials: true }); setDetailFiles(prev => prev.filter(f => f.file_id !== fileId)); toast.success("File removed"); } catch { toast.error("Failed to remove file"); }
  };

  const askAdvisor = async (opp) => {
    setAdvisorLoading(true); setAdvisorResult(null);
    try { const resp = await axios.post(`${API_URL}/api/opportunities/${opp.opportunity_id}/advisor`, {}, { withCredentials: true }); setAdvisorResult(resp.data.analysis); } catch { toast.error("AI analysis failed"); } finally { setAdvisorLoading(false); }
  };

  const convertToAsset = (opp) => {
    const params = new URLSearchParams({ name: opp.name || "", asset_type: "manual", purchase_price: opp.price?.replace(/[^0-9.]/g, "") || "", currency: opp.currency || "USD", notes: `Converted from opportunity: ${opp.name}. ${opp.description || ""} ${opp.notes || ""}`.trim(), category: opp.custom_type || opp.category || "" });
    window.location.href = `/assets?convert=${params.toString()}`;
  };

  const saveToMyDeals = async (opp, category) => {
    const key = `${opp.name}_${category}`;
    if (saving) return; setSaving(key);
    try {
      const payload = { name: opp.name || opp.protocol || opp.symbol || "Untitled", category, protocol: opp.protocol || "", chain: opp.chain || "", apy: opp.apy || 0, tvl: opp.tvl || 0, token: opp.token || opp.symbol || "", risk_score: opp.risk_score || opp.riskScore || 50, url: opp.url || opp.link || "", description: opp.description || "", image_url: opp.image_url || opp.logo || "", expected_return: opp.expected_return || (opp.apy ? `${opp.apy}% APY` : ""), notes: opp.notes || "", price: opp.price || "", currency: opp.currency || "USD", custom_type: category };
      await createOpportunity(payload); toast.success(`Saved "${payload.name}" to My Deals`); fetchMyDeals();
    } catch { toast.error("Failed to save"); } finally { setSaving(null); }
  };

  const getPortfolioFit = async (pick) => {
    try {
      const res = await axios.post(`${API_URL}/api/portfolio/fit-score`, {
        asset_type: "stock",
        risk_score: pick.risk_score || 50,
      }, { withCredentials: true });
      const d = res.data;
      const colorMap = { "Strong Fit": "text-green-500", "Good Fit": "text-green-400", "Neutral": "text-muted-foreground", "Low Fit": "text-yellow-500" };
      return { label: d.label, color: colorMap[d.label] || "text-muted-foreground", desc: d.reasons?.[0] || "", score: d.score };
    } catch {
      return { label: "N/A", color: "text-muted-foreground", desc: "Unable to compute", score: 0 };
    }
  };

  // Alternatives filtering
  const filteredAlternatives = alternatives.filter((alt) => {
    if (altCategoryFilter !== "all" && alt.category !== altCategoryFilter) return false;
    if (altTypeFilter !== "all" && alt.listing_type !== altTypeFilter) return false;
    if (altSearchQuery) { const q = altSearchQuery.toLowerCase(); if (!alt.name?.toLowerCase().includes(q) && !alt.description?.toLowerCase().includes(q)) return false; }
    if (altMinTicket !== "all") { const threshold = parseInt(altMinTicket); const raw = (alt.ticket_min || "").replace(/[$,\s]/g, ""); const val = parseInt(raw); if (!isNaN(val) && val < threshold) return false; }
    return true;
  });

  const stats = {
    total: opportunities.length,
    avgApy: opportunities.length > 0 ? (opportunities.reduce((sum, o) => sum + (o.apy || 0), 0) / opportunities.length).toFixed(2) : 0,
    totalTvl: opportunities.reduce((sum, o) => sum + (o.tvl || 0), 0),
    _page: currentPage, _setPage: setCurrentPage,
  };

  if (portfolioLoading) return <PageLoader />;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="opportunities-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">{t("opps.title")}</h1>
            <p className="text-muted-foreground mt-1">{t("opps.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={loading} data-testid="refresh-opportunities-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />{t("opps.refresh")}
            </Button>
            <Button data-testid="add-opportunity-btn" disabled={isDemo} className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider text-xs glow-primary" onClick={() => { setFormData(f => ({ ...f, category: activeChannel })); setIsAddOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />{t("opps.addManual")}
            </Button>
          </div>
        </div>

        {/* Channel Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {visibleChannels.map((channel) => (
            <Button key={channel.id} variant={activeChannel === channel.id ? "default" : "outline"} className={`flex items-center gap-2 ${activeChannel === channel.id ? "bg-primary text-primary-foreground" : ""}`} onClick={() => setActiveChannel(channel.id)} data-testid={`channel-${channel.id}`}>
              <channel.icon className="w-4 h-4" />{t(channel.labelKey)}
            </Button>
          ))}
        </div>

        <RecommendedSection opps={recommendedOpps} t={t} />

        {activeChannel === "my_deals" && <MyDealsTab myDeals={myDeals} myDealsLoading={myDealsLoading} openDetail={openDetail} openEdit={openEdit} confirmDelete={confirmDelete} setActiveChannel={setActiveChannel} isDemo={isDemo} setFormData={setFormData} setIsAddOpen={setIsAddOpen} visibleChannels={userVisibleChannels} />}
        {activeChannel === "defi" && <DefiTab opportunities={opportunities} stats={stats} minTvl={minTvl} setMinTvl={setMinTvl} tokenFilter={tokenFilter} setTokenFilter={setTokenFilter} minApy={minApy} setMinApy={setMinApy} maxApy={maxApy} setMaxApy={setMaxApy} riskFilter={riskFilter} setRiskFilter={setRiskFilter} sortBy={sortBy} setSortBy={setSortBy} loading={loading} handleRefresh={handleRefresh} saveToMyDeals={saveToMyDeals} confirmDelete={confirmDelete} isDemo={isDemo} t={t} />}
        {activeChannel === "stocks" && <StocksTab stockTrends={stockTrends} stocksLastRefresh={stocksLastRefresh} stocksLoading={stocksLoading} stocksRegionFilter={stocksRegionFilter} setStocksRegionFilter={setStocksRegionFilter} eurUsdHistory={eurUsdHistory} handleStocksRefresh={handleStocksRefresh} saveToMyDeals={saveToMyDeals} getPortfolioFit={getPortfolioFit} isDemo={isDemo} t={t} userPreferredRegions={userRegions} />}
        {activeChannel === "commodities" && <CommoditiesTab saveToMyDeals={saveToMyDeals} isDemo={isDemo} t={t} />}
        {activeChannel === "web3_secondaries" && <Web3SecondariesTab alternatives={alternatives} filteredAlternatives={filteredAlternatives} altLastRefresh={altLastRefresh} altLoading={altLoading} altCategoryFilter={altCategoryFilter} setAltCategoryFilter={setAltCategoryFilter} altTypeFilter={altTypeFilter} setAltTypeFilter={setAltTypeFilter} altSearchQuery={altSearchQuery} setAltSearchQuery={setAltSearchQuery} altMinTicket={altMinTicket} setAltMinTicket={setAltMinTicket} handleAltRefresh={handleAltRefresh} saveToMyDeals={saveToMyDeals} isDemo={isDemo} t={t} />}
        {activeChannel === "others" && <OthersTab othersOpps={othersOpps} openDetail={openDetail} openEdit={openEdit} confirmDelete={confirmDelete} isDemo={isDemo} setFormData={setFormData} setIsAddOpen={setIsAddOpen} />}

        {/* Delete Confirmation */}
        <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <DialogContent><DialogHeader><DialogTitle className="font-['Barlow_Condensed'] uppercase text-destructive">Delete Opportunity</DialogTitle><DialogDescription>Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.</DialogDescription></DialogHeader>
            <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setIsDeleteOpen(false)}>Cancel</Button><Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <OpportunityFormDialog isOpen={isAddOpen} onOpenChange={(open) => { setIsAddOpen(open); if (!open) { setIsEditMode(false); resetForm(); } }} formData={formData} setFormData={setFormData} isEditMode={isEditMode} onSave={handleSave} isDemo={isDemo} />

        <OpportunityDetailModal isOpen={isDetailOpen} onOpenChange={(open) => { setIsDetailOpen(open); if (!open) setAdvisorResult(null); }} detailOpp={detailOpp} detailFiles={detailFiles} uploading={uploading} onFileUpload={handleFileUpload} onFileDelete={handleFileDelete} advisorLoading={advisorLoading} advisorResult={advisorResult} onAskAdvisor={askAdvisor} onEdit={(opp) => { setIsDetailOpen(false); openEdit(opp); }} onConvertToAsset={(opp) => { setIsDetailOpen(false); convertToAsset(opp); }} onDelete={(opp) => { setIsDetailOpen(false); confirmDelete(opp); }} isDemo={isDemo} />
      </div>
    </MainLayout>
  );
};

export default OpportunitiesPage;
